// Sanningskälla för HUR Fenny får beskriva sin närvaro på en ort.
//
// Bakgrund (lokal SEO, 2026): sidor som påstår lokal närvaro utan
// verifierbar grund riskerar att klassas som doorway pages. Fenny har
// kontor i Stockholm, Västerås och Malmö (se src/content/locations.ts).
// Alla andra orter produceras från något av dessa kontor — och det ska
// stå på sidan, i klartext.
//
// Regel: ortssidor beskriver PRODUKTIONEN på plats ("så här ser det ut
// när vi filmar här"), inte en påhittad lokal etablering.

import { LOCATIONS, type LocationEntity } from "@/content/locations";

export type PresenceKind = "office" | "travel";

export interface CityPresence {
  /** office = fysiskt kontor på orten. travel = vi reser dit. */
  kind: PresenceKind;
  /** Kontorsstaden som utför produktionen. */
  base: string;
  /** Kort etikett för bullets/rubriker. */
  label: string;
  /** En mening som är sann oavsett ort — används i copy och FAQ. */
  line: string;
  /** Namngivna miljöer vi faktiskt filmar i på orten (publika platser). */
  locations: string[];
  /** Restidsnotis, ungefärlig. Tom sträng när kontor finns på orten. */
  travelNote: string;
  /** true = LocalBusiness-schema får emitteras (kontor finns). */
  allowLocalBusiness: boolean;
}

/**
 * Namngivna, publikt verifierbara inspelningsmiljöer per ort.
 * Används för att ersätta generisk mallcopy med platsspecifik text.
 */
const ON_LOCATION: Record<string, { locations: string[]; travel?: string }> = {
  stockholm: { locations: ["City och Norrmalm", "Kista Science City", "Hammarby Sjöstad", "Frihamnen", "Slussen och Södermalm"] },
  vasteras: { locations: ["Finnslätten", "Kopparlunden", "hamnen och Mälarstranden", "Västerås city"] },
  malmo: { locations: ["Västra Hamnen", "Hyllie", "Malmö Universitet", "Norra Sorgenfri", "Öresundsbron från Limhamn"] },
  goteborg: { locations: ["Lindholmen Science Park", "Gullbergsvass och Hisingen", "Frihamnen", "Nordstan och Avenyn"], travel: "ca 5 h från Stockholm — vi lägger inspelningen i block över två dagar" },
  uppsala: { locations: ["Uppsala Business Park", "Ångströmlaboratoriet-området", "Fyrisån och centrum", "Boländerna"], travel: "ca 45 min från Stockholm" },
  solna: { locations: ["Hagastaden och Karolinska", "Solna Business Park", "Arenastaden", "Hagaparken"], travel: "ca 20 min från Stockholmskontoret" },
  linkoping: { locations: ["Mjärdevi Science Park", "Linköpings universitet", "Saab-området", "Stångån och centrum"], travel: "ca 2 h från Stockholm" },
  norrkoping: { locations: ["Industrilandskapet", "Campus Norrköping", "Hamnen och Bråviken", "Butängen"], travel: "ca 2 h från Stockholm" },
  orebro: { locations: ["Örebro universitet", "Aspholmen och Boglundsängen", "slottet och Svartån", "logistikområdet Törsjö"], travel: "ca 1 h 15 min från Västerås" },
  eskilstuna: { locations: ["Munktellstaden", "Eskilstuna Logistikpark", "Fristadstorget och centrum"], travel: "ca 45 min från Västerås" },
  gavle: { locations: ["Gävle Strand och hamnen", "Näringen och Sätra industriområde", "Gävle slott och Gamla Gefle", "Högskolan i Gävle"], travel: "ca 1 h 45 min från Stockholm" },
  sundsvall: { locations: ["Stenstan", "Tunadalshamnen och industristråket", "Sundsvallsbron", "Mittuniversitetet"], travel: "ca 4 h från Stockholm — normalt två inspelningsdagar per resa" },
  umea: { locations: ["Umeå universitet och Universitetsstaden", "Umestan Företagspark", "Ön och Umeälven", "hamnen i Holmsund"], travel: "flyg från Stockholm, inspelning samlas till 1–2 dagar" },
  lulea: { locations: ["Luleå tekniska universitet", "Aurorum Science Park", "hamnen och Svartön", "Gammelstads kyrkstad"], travel: "flyg från Stockholm, inspelning samlas till 1–2 dagar" },
  skelleftea: { locations: ["Site Northvolt-området", "Sara kulturhus", "hamnen i Skelleftehamn"], travel: "flyg till Skellefteå eller Umeå, 1–2 inspelningsdagar per resa" },
  ostersund: { locations: ["Storsjön och Frösön", "Mittuniversitetet", "Campus och centrum"], travel: "ca 6 h från Stockholm — normalt två inspelningsdagar" },
  jonkoping: { locations: ["Jönköping University", "Munksjön och A6-området", "Torsvik logistikområde", "Vättern-stranden"], travel: "ca 3 h från Malmö" },
  vaxjo: { locations: ["Linnéuniversitetet", "Videum Science Park", "Växjösjön och centrum"], travel: "ca 2 h från Malmö" },
  kalmar: { locations: ["Kalmar slott och Kvarnholmen", "Linnéuniversitetets campus", "Ölandsbron"], travel: "ca 3 h från Malmö" },
  karlskrona: { locations: ["örlogsstaden och Stumholmen", "Blekinge Tekniska Högskola", "Trossö"], travel: "ca 2 h 30 min från Malmö" },
  helsingborg: { locations: ["Oceanhamnen och H+", "Berga industriområde", "Kärnan och centrum", "hamnen mot Helsingör"], travel: "ca 45 min från Malmö" },
  lund: { locations: ["Ideon Science Park", "Medicon Village", "Lunds universitet", "MAX IV och Brunnshög"], travel: "ca 20 min från Malmökontoret" },
  kristianstad: { locations: ["Näsby campus", "Åhus och Helge å", "centrum och Tivoliparken"], travel: "ca 1 h från Malmö" },
  halmstad: { locations: ["Högskolan i Halmstad", "Halmstad hamn", "Tylösand och Nissan"], travel: "ca 1 h 30 min från Malmö" },
  varberg: { locations: ["Varbergs fästning", "hamnen och Västkustbanan-området", "Getterön"], travel: "ca 2 h från Malmö" },
  boras: { locations: ["Textile Fashion Center", "Viared industriområde", "Högskolan i Borås"], travel: "ca 1 h från Göteborg, där vi har crew — produktionsledning från Stockholm" },
  skovde: { locations: ["Gothia Science Park", "Högskolan i Skövde", "Billingen"], travel: "ca 2 h från Göteborg, där vi har crew" },
  trollhattan: { locations: ["Innovatum Science Park", "slussarna i Trollhättan", "NEVS-området"], travel: "ca 1 h från Göteborg, där vi har crew" },
  uddevalla: { locations: ["hamnen och Bäveån", "Kampenhof", "Gustafsberg"], travel: "ca 1 h 15 min från Göteborg, där vi har crew" },
  karlstad: { locations: ["Karlstads universitet", "Inre hamn", "Vänerstranden och Klarälven"], travel: "ca 3 h från Västerås" },
  falun: { locations: ["Falu gruva (världsarv)", "Lugnet", "Högskolan Dalarna"], travel: "ca 2 h från Västerås" },
  borlange: { locations: ["SSAB-området", "Kupolen och centrum", "Dalälven"], travel: "ca 2 h från Västerås" },
  nykoping: { locations: ["Nyköpingshus och ån", "Skavsta-området", "Brandkärr och hamnen"], travel: "ca 1 h 15 min från Stockholm" },
  sodertalje: { locations: ["Scania-området", "Södertälje hamn", "Campus Telge"], travel: "ca 40 min från Stockholmskontoret" },
  visby: { locations: ["ringmuren och innerstaden (världsarv)", "Visby hamn", "Almedalen"], travel: "flyg eller färja, inspelning samlas till 1–2 dagar" },
  kista: { locations: ["Kista Science Tower", "Kistagången och Electrum", "Kista Galleria"], travel: "ca 20 min från Stockholmskontoret" },
  sundbyberg: { locations: ["Sundbybergs centrum", "Ursvik", "Hallonbergen"], travel: "ca 15 min från Stockholmskontoret" },
  nacka: { locations: ["Nacka Strand", "Sickla köpkvarter", "Kvarnholmen"], travel: "ca 20 min från Stockholmskontoret" },
  taby: { locations: ["Täby Centrum", "Arninge", "Näsbypark"], travel: "ca 30 min från Stockholmskontoret" },
  sodermalm: { locations: ["Hornstull och Långholmen", "SoFo", "Slussen och Fotografiska-området"], travel: "ca 10 min från Stockholmskontoret" },
  molndal: { locations: ["AstraZeneca-området i Mölndal", "Forsåker", "Kållered"], travel: "crew i Göteborg på plats, produktionsledning från Stockholm" },
  hisingen: { locations: ["Lindholmen", "Volvo Torslanda-området", "Backaplan"], travel: "crew i Göteborg på plats, produktionsledning från Stockholm" },
  lindholmen: { locations: ["Lindholmen Science Park", "Älvrummet", "Eriksberg"], travel: "crew i Göteborg på plats, produktionsledning från Stockholm" },
  hyllie: { locations: ["Malmö Arena och Hyllie stationsområde", "Emporia", "Malmömässan"], travel: "ca 10 min från Malmökontoret" },
};

const DEFAULT_LOCATIONS = [
  "er arbetsplats",
  "en location vi scoutar åt er",
  "hyrd studio i regionen",
];

function baseFor(entity: LocationEntity | undefined): string {
  if (!entity) return "Stockholm";
  if (entity.type === "office") return entity.city;
  return entity.servedFrom ?? "Stockholm";
}

/**
 * Returnerar hur en ort får beskrivas. Alltid sant, aldrig påstådd
 * etablering. Okända orter faller tillbaka på "reser från Stockholm".
 */
export function presenceFor(slug: string, name: string): CityPresence {
  const entity = LOCATIONS[slug];
  const isOffice = entity?.type === "office";
  const hasLocalCrew = entity?.type === "local_team";
  const base = baseFor(entity);
  const facts = ON_LOCATION[slug];
  const locations = facts?.locations?.length ? facts.locations : DEFAULT_LOCATIONS;
  const travelNote = isOffice ? "" : (facts?.travel ?? `Teamet reser från ${base} och är på plats hela inspelningsdagen.`);

  return {
    kind: isOffice ? "office" : "travel",
    base,
    label: isOffice
      ? `Kontor i ${name}`
      : hasLocalCrew
        ? `Crew i ${name}, produktionsledning från ${base}`
        : `På plats från ${base}`,
    line: isOffice
      ? `Fenny har kontor i ${name} — teamet utgår härifrån och full produktionskapacitet finns på orten.`
      : hasLocalCrew
        ? `Vi har inget kontor i ${name}, men crew bosatt på orten. Produktionsledning, klipp och efterarbete sker från ${base}. Resa inom upptagningsområdet ingår i priset.`
        : `Vi har inget kontor i ${name}. Produktionen utförs av teamet från ${base}, som är på plats i ${name} hela inspelningsdagen. Resa inom upptagningsområdet ingår i priset.`,
    locations,
    travelNote,
    allowLocalBusiness: Boolean(isOffice),
  };
}

/** "Ideon Science Park, Medicon Village och Lunds universitet" */
export function locationSentence(presence: CityPresence, max = 4): string {
  const slice = presence.locations.slice(0, max);
  if (slice.length <= 1) return slice.join("");
  return slice.slice(0, -1).join(", ") + " och " + slice.at(-1);
}
